import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { getUploadUrl, uploadPhoto } from '../lib/api';
import { cleanBackground, warmSegmenter } from '../lib/segment';
import { sniffPhotoType } from '../lib/sniff';
import { SUPPORTED_PHOTO_TYPES, type SupportedPhotoType } from '../lib/types';
import { useCustomerSession } from './CustomerSession';
import { presentUploadFailure } from './errors';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';

/**
 * Photo capture, review, and direct-to-R2 upload.
 *
 * Camera first, with a file picker as a second path. Two things about the
 * `capture` attribute that only show up on a device: it is a *hint*, so the OS
 * camera app still lets the user flip lenses, and it is ignored entirely on
 * desktop, where the same input behaves as an ordinary file picker.
 *
 * `accept` is also only a hint — some pickers let the user choose anything — so
 * the real type is validated after selection. That matters more than usual
 * here: the declared type is signed into the presigned PUT URL, and a mismatch
 * fails at R2 with an opaque 403 `SignatureDoesNotMatch` rather than a useful
 * message.
 */

const ACCEPT = SUPPORTED_PHOTO_TYPES.join(',');

function normaliseType(type: string): SupportedPhotoType | null {
  const lower = type.toLowerCase();
  if (lower === 'image/jpg') return 'image/jpeg';
  return (SUPPORTED_PHOTO_TYPES as readonly string[]).includes(lower)
    ? (lower as SupportedPhotoType)
    : null;
}

/**
 * HEIC cannot be rendered by `<img>` outside Apple platforms. iOS Safari
 * usually transcodes to JPEG when a photo is picked, but not always — and the
 * upload and the generation are both fine either way, so a failed preview must
 * not read as a failed capture.
 */
function isPreviewable(type: SupportedPhotoType): boolean {
  return type === 'image/jpeg' || type === 'image/png' || type === 'image/webp';
}

export function CaptureScreen() {
  const navigate = useNavigate();
  const { photoKey: currentPhotoKey, setPhotoKey } = useCustomerSession();

  // Reached from the try-on screen's Retake as well as from the nav, and the
  // wording is the only thing that differs.
  const isRetake = currentPhotoKey !== null;

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  /**
   * ⚠️ The type decided at pick time, held rather than re-derived at upload.
   * It is what gets signed into the presigned url *and* sent as the PUT header,
   * and for a file the picker mislabelled it is not recoverable from
   * `file.type` at all.
   */
  const [photoType, setPhotoType] = useState<SupportedPhotoType | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  /**
   * `detail` is only ever set for an upload failure. The upload happens
   * device→R2 with nothing server-side in the path, so this line is the only
   * place the cause is ever stated — see `presentUploadFailure`.
   */
  const [error, setError] = useState<{ message: string; detail?: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  /** Set while the background is being removed, between picking and preview. */
  const [preparing, setPreparing] = useState(false);

  /**
   * Start fetching the ~12MB segmentation runtime the moment this screen opens,
   * so the download overlaps the customer framing their shot instead of landing
   * after they press the shutter. It is cached by the service worker, so this
   * only costs anything on a device's first visit. Never throws.
   */
  useEffect(() => {
    warmSegmenter();
  }, []);

  async function onPick(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const picked = event.target.files?.[0];
    // Reset so picking the same file twice still fires a change event.
    event.target.value = '';
    if (!picked) return;

    /**
     * ⚠️ An empty file is its own failure and must not read as a type problem.
     * A Google Photos or Drive entry whose backing download failed arrives as a
     * real `File` of length zero — it would sniff as nothing, upload as nothing,
     * and produce a garbled generation rather than an error.
     */
    if (picked.size === 0) {
      setError({
        message: 'That photo could not be read. Please take a new one with the camera.',
        detail: `empty file — "${picked.name || 'unnamed'}" arrived with 0 bytes, usually a cloud photo that failed to download`,
      });
      return;
    }

    /**
     * ⚠️ **The declared type is a fallback, not the answer.** Google Photos and
     * Drive-backed pickers routinely hand over `''` or
     * `application/octet-stream`, and the old code rejected those outright —
     * telling the customer an ordinary JPEG was an unsupported format. The bytes
     * decide; `picked.type` only gets a say when they are unreadable.
     */
    const type = (await sniffPhotoType(picked)) ?? normaliseType(picked.type);

    if (!type) {
      setError({
        message:
          'That file type is not supported. Please use a photo taken with the camera, or a JPEG, PNG, WEBP or HEIC image.',
        // On a phone there is no console to check, so the evidence has to be on
        // screen — this line is the whole diagnosis for a picker we have not met.
        detail: `unrecognised image — declared "${picked.type || 'nothing'}", ${picked.size} bytes, name "${picked.name || 'unnamed'}"`,
      });
      return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setError(null);

    /**
     * Background removal, on-device, *before* the preview rather than during
     * upload — so the image the customer approves is the image that is sent.
     * Doing it at confirm time would show them one photo and upload another.
     *
     * ⚠️ **A null result is not an error.** The device may be unable to decode
     * the file (HEIC outside Safari) or to run the model at all; the original
     * then goes up untouched and the visit proceeds. Nothing is said about it,
     * because there is nothing the customer could do differently.
     */
    setPreparing(true);
    const cleaned = await cleanBackground(picked);
    setPreparing(false);

    /**
     * ⚠️ The type moves with the file. `cleanBackground` always returns JPEG
     * regardless of what went in, and this is the value that gets signed into
     * the presigned url — declaring the *picked* file's type here would be the
     * `SignatureDoesNotMatch` trap described at the top of this file.
     */
    const finalFile = cleaned
      ? new File([cleaned], picked.name, { type: 'image/jpeg' })
      : picked;
    const finalType: SupportedPhotoType = cleaned ? 'image/jpeg' : type;

    setPreviewFailed(!isPreviewable(finalType));
    setFile(finalFile);
    setPhotoType(finalType);
    setPreviewUrl(isPreviewable(finalType) ? URL.createObjectURL(finalFile) : null);
  }

  function discard(): void {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewFailed(false);
    setFile(null);
    setPhotoType(null);
    setError(null);
  }

  async function confirm(): Promise<void> {
    if (!file || !photoType || uploading) return;
    const type = photoType;

    setUploading(true);
    setError(null);
    try {
      const { uploadUrl, photoKey } = await getUploadUrl(type);
      /**
       * Straight to R2. The photo bytes never pass through the API server.
       *
       * ⚠️ `type`, not `file.type` — the value decided at pick time and just
       * signed into `uploadUrl`. `Content-Type` is folded into the SigV4
       * signature, so the two must be the same string or R2 answers an opaque
       * `403 SignatureDoesNotMatch`. For a file the picker mislabelled, that
       * string exists nowhere on `file` at all.
       */
      await uploadPhoto(uploadUrl, file, type);

      /**
       * 🔒 The file is handed over as well as the key: R2 has the bytes now, but
       * in the **private** bucket, and the browser cannot read them back without
       * a presigned GET endpoint that does not exist. The device-local copy the
       * session writes is the whole mechanism behind "view your own photo", and
       * `endVisit` deleting it is what keeps a face photo (G28) from outliving
       * the visit.
       */
      await setPhotoKey(photoKey, file);
      discard();
      void navigate('/try-on', { replace: true });
    } catch (err) {
      const failure = presentUploadFailure(err);
      // Nothing server-side witnesses this request, so the console is the only
      // other record that it happened at all.
      console.error('[capture] upload failed:', failure.detail, err);
      setError(failure);
    } finally {
      setUploading(false);
    }
  }

  return (
    // Viewport box and safe-area inset live on the customer layout route now.
    <div className="flex flex-1 flex-col">
      <header className="flex items-center gap-3 px-4 py-3">
        {/*
          A retake came from the try-on screen and must go back to it; a first
          capture came from the nav, where `-1` would bounce between sections.
        */}
        <button
          type="button"
          onClick={() => void navigate(isRetake ? '/try-on' : '/', { replace: true })}
          className="min-h-(--size-touch-min) px-2 text-sm font-medium text-secondary"
        >
          {isRetake ? 'Cancel' : 'Back'}
        </button>
        <h1 className="text-lg font-semibold text-primary">
          {isRetake ? 'Retake your photo' : 'Take a photo'}
        </h1>
      </header>

      <div className="flex flex-1 flex-col gap-4 px-4 pb-4">
        {file && (previewUrl || previewFailed) ? (
          <>
            <div className="flex flex-1 items-center justify-center overflow-hidden rounded-lg bg-inverse">
              {previewUrl && !previewFailed ? (
                <img
                  src={previewUrl}
                  alt="Your photo"
                  className="max-h-full max-w-full object-contain"
                  onError={() => setPreviewFailed(true)}
                />
              ) : (
                <p className="p-8 text-center text-sm text-on-inverse">
                  Preview not available for this image format — your photo is fine and will still
                  work.
                </p>
              )}
            </div>

            {error && (
              <Alert tone="danger">
                {error.message}
                {error.detail && (
                  <span className="mt-1 block font-mono text-xs opacity-70">{error.detail}</span>
                )}
              </Alert>
            )}

            <div className="flex gap-3 pb-safe-action">
              <Button variant="secondary" onClick={discard} disabled={uploading || preparing} fullWidth>
                Retake
              </Button>
              <Button onClick={() => void confirm()} loading={uploading} fullWidth>
                Use this photo
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-(--color-border-default) p-8 text-center">
              <p className="text-lg font-medium text-primary">Ready when you are</p>
              <p className="text-sm text-secondary">
                Stand facing the camera with your full outfit in frame.
              </p>
              {isRetake && (
                <p className="text-sm text-secondary">
                  The try-on images you have already generated will be kept.
                </p>
              )}
            </div>

            {error && (
              <Alert tone="danger">
                {error.message}
                {error.detail && (
                  <span className="mt-1 block font-mono text-xs opacity-70">{error.detail}</span>
                )}
              </Alert>
            )}

            {/*
              The consent line (G28). Face photos of members of the public are
              personal data under India's DPDP Act. Retention is handled by
              deleting the customer-photos prefix daily; this is the other half
              — telling the customer it is happening at the moment it happens.
            */}
            <p className="text-center text-xs text-tertiary">
              Your photo is used only to create your try-on images and is deleted from our system
              daily. It is never shared.
            </p>

            <div className="flex flex-col gap-3 pb-safe-action">
              <Button
                onClick={() => cameraInputRef.current?.click()}
                loading={preparing}
                fullWidth
              >
                {preparing ? 'Preparing your photo' : 'Open camera'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={preparing}
                fullWidth
              >
                Choose an existing photo
              </Button>
            </div>
          </>
        )}
      </div>

      {/*
        Two inputs rather than one. `capture` on a single input removes the
        gallery option on most Android builds, so the fallback needs its own
        input with no `capture` attribute.
      */}
      <input
        ref={cameraInputRef}
        type="file"
        accept={ACCEPT}
        capture="environment"
        onChange={(event) => void onPick(event)}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        onChange={(event) => void onPick(event)}
        className="hidden"
      />
    </div>
  );
}
